import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MediaTypes, resolveError, useMediaCreateMutation, useMediaPreviewMutation } from "api";
import { clearTempUrl, getTempUrl } from "helpers";

export const useMedia = (type: MediaTypes, apiPreview: boolean = true, isFile: boolean = false) => {
  const [mediaCreate, { loading: loadingCreate }] = useMediaCreateMutation();
  const [mediaPreview, { loading: loadingPreview }] = useMediaPreviewMutation();

  const loading = useMemo(() => loadingCreate || loadingPreview, [loadingCreate, loadingPreview]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | undefined>(undefined);
  const [preview, setPreview] = useState<string | undefined>(undefined);

  const openFileBrowser = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleChangeFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || !e.target.files[0]) return;

      const file = e.target.files[0];

      setFile(file);

      if (!isFile) clearTempUrl(preview);

      if (!apiPreview && !isFile) return setPreview(getTempUrl(file));

      if (isFile) return setPreview(file.name);

      try {
        const { data } = await mediaPreview({ variables: { input: { file, type } } });

        setPreview(data?.mediaPreview.preview);
      } catch (e) {
        setPreview(getTempUrl(file));
      }
    },
    [isFile, preview, apiPreview, mediaPreview, type]
  );

  const upload = useCallback(async () => {
    if (!preview || !file) {
      return undefined;
    }

    try {
      const { data } = await mediaCreate({ variables: { input: { file, type } } });

      return data?.mediaCreate.media.id;
    } catch (e: any) {
      resolveError(e);
    }

    return undefined;
  }, [file, mediaCreate, preview, type]);

  const clear = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setPreview(undefined);
    setFile(undefined);
  }, []);

  const fileInputProps = useMemo(
    () => ({
      ref: fileInputRef,
      type: "file",
      accept: !isFile ? "image/*" : undefined,
      hidden: true,
      multiple: false,
      onChange: handleChangeFile,
    }),
    [handleChangeFile, isFile]
  );

  useEffect(
    () => () => {
      if (!isFile) clearTempUrl(preview);
    },
    [isFile, preview]
  );

  return {
    loading,
    preview,
    fileInputProps,
    openFileBrowser,
    upload,
    clear,
  };
};

export type Media = ReturnType<typeof useMedia>;
